import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import {
  BUSCAR_USUARIOS_UNIFICADOS_EVENTO,
  CONTEXTO_PUNTO_VENTA_EVENTO,
  DOCUMENTOS_IMPRESION_VENTA_EVENTO,
  INICIAR_VENTA_EVENTO,
  VALIDAR_DISPONIBILIDAD_PRODUCTOS_EVENTO,
  VENTAS_RECIENTES_EVENTO,
} from "services/ventaEvento";
import {
  filtrarUsuariosUnificadosPorDni,
  getIconoMedioPago,
  mapUsuarioUnificadoComprador,
  mapProductoCatalogo,
  mapVentaReciente,
  mapVoucherFisico,
  referenciaCompradorEvento,
} from "../presentacion";
import { calculateSaleTotals, canAddProductByStock, getVoucherAvailableValue } from "../utils";

const createConsumer = (dni = "") => ({
  id: null,
  dni,
  firstName: "",
  lastName: "",
  communityRole: "",
  isCommunityMember: false,
  coincidencias: 0,
});

// Identifica el intento de cobro. La misma clave nunca crea dos ventas: si se
// repite, el backend devuelve la operación original.
const nuevaClaveIdempotencia = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `evt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
};

const getErrorCode = (error) => {
  const graphqlError = (error?.graphQLErrors || error?.errors || [])[0];
  return (
    graphqlError?.extensions?.code ||
    graphqlError?.originalError?.code ||
    graphqlError?.code ||
    error?.code ||
    null
  );
};

// Adaptador real del punto de venta. Expone el mismo contrato que el hook de la
// demo para que los componentes no cambien, pero evento, catálogo, precios,
// comprador y venta vienen del backend.
export default function useVentaEventoReal({ skip = false } = {}) {
  const [cart, setCart] = useState({});
  const [selectionOrder, setSelectionOrder] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [customerCandidates, setCustomerCandidates] = useState([]);
  const [paymentCode, setPaymentCode] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [customerError, setCustomerError] = useState("");
  const [customerPricingLoading, setCustomerPricingLoading] = useState(false);
  const [compradorContexto, setCompradorContexto] = useState(null);
  const [catalogNotice, setCatalogNotice] = useState("");
  // Aclaraciones del cliente para la comanda; viajan con la venta y las
  // imprime la cocina. Viven acá y no en el paso 2, que se remonta al
  // identificar al comprador.
  const [observaciones, setObservaciones] = useState("");
  const [claveIdempotencia, setClaveIdempotencia] = useState(nuevaClaveIdempotencia);
  const [disponibilidadInicial, setDisponibilidadInicial] = useState({});

  const {
    data,
    previousData,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useQuery(CONTEXTO_PUNTO_VENTA_EVENTO, {
    fetchPolicy: "cache-and-network",
    variables: {
      comprador: compradorContexto,
    },
    skip,
  });
  const { data: dataVentas, refetch: refetchVentas } = useQuery(
    VENTAS_RECIENTES_EVENTO,
    { fetchPolicy: "cache-and-network", variables: { limite: 20 }, skip }
  );
  const [lookupUsuarios, { loading: customerLookupLoading }] = useLazyQuery(
    BUSCAR_USUARIOS_UNIFICADOS_EVENTO,
    { fetchPolicy: "network-only" }
  );
  const [ejecutarVenta, { loading: saleLoading }] = useMutation(INICIAR_VENTA_EVENTO);
  const [validarDisponibilidadInicial] = useLazyQuery(
    VALIDAR_DISPONIBILIDAD_PRODUCTOS_EVENTO,
    { fetchPolicy: "no-cache" }
  );
  // Reimpresión: siempre desde base, nunca desde lo que quedó en memoria.
  const [consultarDocumentos] = useLazyQuery(DOCUMENTOS_IMPRESION_VENTA_EVENTO, {
    fetchPolicy: "network-only",
  });

  // Apollo vuelve a marcar `loading` durante polling, refetch y cambios de
  // comprador. En esas actualizaciones conservamos el último contexto válido
  // y reservamos la pantalla de carga para la primera consulta de la pestaña.
  const contexto =
    data?.contextoPuntoVentaEvento ??
    previousData?.contextoPuntoVentaEvento ??
    null;
  const loadingInicial = loading && !contexto;

  // El cronograma puede cambiar de fecha o de turno mientras el puesto queda
  // abierto. Se actualiza en segundo plano solo si no existe una venta armada.
  useEffect(() => {
    if (skip) return undefined;
    if (Object.keys(cart).length === 0) startPolling(60 * 1000);
    else stopPolling();
    return () => stopPolling();
  }, [cart, skip, startPolling, stopPolling]);

  useEffect(() => {
    if (skip || typeof window === "undefined") return undefined;
    const handleFocus = () => {
      if (Object.keys(cart).length !== 0) return;
      refetch({
        comprador: compradorContexto,
      });
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [cart, compradorContexto, refetch, skip]);

  const catalogProducts = useMemo(
    () => (contexto?.productos || []).map(mapProductoCatalogo),
    [contexto]
  );

  useEffect(() => {
    if (skip || catalogProducts.length === 0 || !contexto?.sede?.id) {
      setDisponibilidadInicial({});
      return undefined;
    }

    setDisponibilidadInicial({});
    let isActive = true;
    validarDisponibilidadInicial({
      variables: {
        productos: catalogProducts.map((product) => ({
          idProducto: product.id,
          cantidad: 1,
          esElaborado: product.esElaborado,
        })),
        idSucursal: contexto.sede.id,
      },
    })
      .then(({ data: disponibilidadData }) => {
        if (!isActive) return;
        const siguiente = {};
        (disponibilidadData?.validarDisponibilidadProductos || []).forEach((item) => {
          siguiente[Number(item.idProducto)] = Number(item.cantidadDisponible || 0);
        });
        setDisponibilidadInicial(siguiente);
      })
      .catch(() => {
        if (isActive) setDisponibilidadInicial({});
      });

    return () => {
      isActive = false;
    };
  }, [catalogProducts, contexto?.sede?.id, skip, validarDisponibilidadInicial]);

  const products = useMemo(
    () =>
      catalogProducts.map((product) => ({
        ...product,
        stockTeorico: disponibilidadInicial[product.id],
      })),
    [catalogProducts, disponibilidadInicial]
  );

  const paymentMethods = useMemo(
    () =>
      (contexto?.mediosPago || []).map((medio) => ({
        code: medio.codigo,
        label: medio.etiqueta,
        icon: getIconoMedioPago(medio.codigo),
        requiresSellerConfirmation: Boolean(medio.requiereConfirmacionVendedor),
        recommended: Boolean(medio.recomendado),
      })),
    [contexto]
  );

  const recentSales = useMemo(
    () => (dataVentas?.ventasRecientesEvento || []).map(mapVentaReciente),
    [dataVentas]
  );

  const vouchers = useMemo(
    () => (contexto?.vouchersFisicos || []).map(mapVoucherFisico),
    [contexto]
  );
  const voucher = vouchers.find((item) => item.code === voucherCode) || null;

  const cartItems = useMemo(
    () =>
      products
        .filter((product) => cart[product.id])
        .map((product) => ({ ...product, quantity: cart[product.id] })),
    [cart, products]
  );

  // Estimación de pantalla. La cobertura definitiva la calcula el backend al
  // cobrar, con la misma regla: primeras unidades elegibles hasta la pieza.
  const totals = useMemo(
    () => calculateSaleTotals({ cartItems, voucher, selectionOrder, porcentajeDescuentoEvento: contexto?.porcentajeDescuentoEvento, menuAplicaDescuento: contexto?.evento?.descuentosClientes?.menuUniversitarioAplicaDescuento !== false, comunidad: Boolean(customer?.isCommunityMember) }),
    [cartItems, selectionOrder, voucher, contexto, customer]
  );

  const avisos = useMemo(
    () => [
      ...(contexto?.avisos || []),
      ...(catalogNotice
        ? [{ codigo: "EVENTO_COMEDOR_CATALOGO_ACTUALIZADO", mensaje: catalogNotice }]
        : []),
    ],
    [catalogNotice, contexto?.avisos]
  );

  const reconciliarCarritoConContexto = (contextoActualizado) => {
    const idsDisponibles = new Set(
      (contextoActualizado?.productos || []).map((producto) =>
        Number(producto.idProducto)
      )
    );
    const idsQuitados = Object.keys(cart)
      .map(Number)
      .filter((idProducto) => !idsDisponibles.has(idProducto));
    if (!idsQuitados.length) return [];

    const idsQuitadosSet = new Set(idsQuitados);
    setCart((current) =>
      Object.fromEntries(
        Object.entries(current).filter(
          ([idProducto]) => !idsQuitadosSet.has(Number(idProducto))
        )
      )
    );
    setSelectionOrder((current) =>
      current.filter((idProducto) => !idsQuitadosSet.has(Number(idProducto)))
    );
    setCatalogNotice(
      "El cronograma o la tarifa del menú cambió. Se retiraron del carrito los productos que ya no están disponibles; revisá la venta antes de continuar."
    );
    return idsQuitados;
  };

  const addProduct = (productId) => {
    const product = products.find((item) => Number(item.id) === Number(productId));
    const cantidadActual = Number(cart[productId] || 0);
    if (!canAddProductByStock(product?.stockTeorico, cantidadActual)) return false;

    setCart((current) => ({ ...current, [productId]: (current[productId] || 0) + 1 }));
    setSelectionOrder((current) => [...current, productId]);
    return true;
  };

  const changeQuantity = (productId, amount) => {
    if (amount > 0) {
      addProduct(productId);
      return;
    }

    setCart((current) => {
      const nextQuantity = Math.max((current[productId] || 0) + amount, 0);
      const next = { ...current };
      if (nextQuantity === 0) delete next[productId];
      else next[productId] = nextQuantity;
      return next;
    });
    setSelectionOrder((current) => {
      const lastIndex = current.lastIndexOf(productId);
      return lastIndex < 0
        ? current
        : [...current.slice(0, lastIndex), ...current.slice(lastIndex + 1)];
    });
  };

  const actualizarContextoComprador = async (nextCustomer = null) => {
    const referencia = referenciaCompradorEvento(nextCustomer);
    const contextoRefrescado = await refetch({
      comprador: referencia,
    });
    reconciliarCarritoConContexto(
      contextoRefrescado?.data?.contextoPuntoVentaEvento
    );
    setCompradorContexto(referencia);
  };

  const selectCustomerCandidate = async (candidate) => {
    setCustomerError("");
    setCatalogNotice("");
    if (!candidate?.id || !candidate?.appUsuario || !candidate?.dni) {
      setCustomerError("No se pudo identificar la aplicacion del usuario seleccionado.");
      return null;
    }

    setCustomerPricingLoading(true);
    try {
      const next = mapUsuarioUnificadoComprador(candidate);
      await actualizarContextoComprador(next);
      setCustomer(next);
      setCustomerCandidates([]);
      return next;
    } catch (contextError) {
      setCustomerError(
        contextError?.message || "No se pudo calcular la tarifa del usuario seleccionado."
      );
      return null;
    } finally {
      setCustomerPricingLoading(false);
    }
  };

  const identifyCustomer = async (rawDni) => {
    const dni = String(rawDni || "").replace(/\D/g, "");
    setCustomer(null);
    setCustomerCandidates([]);
    setCustomerError("");
    setCatalogNotice("");
    setCustomerPricingLoading(true);
    try {
      const { data: resultado } = await lookupUsuarios({ variables: { searchTerm: dni } });
      const candidatos = filtrarUsuariosUnificadosPorDni(
        resultado?.searchUnifiedUsers || [],
        dni
      );

      if (candidatos.length === 0) {
        const consumer = createConsumer(dni);
        await actualizarContextoComprador(consumer);
        setCustomer(consumer);
        return consumer;
      }
      if (candidatos.length === 1) {
        const next = mapUsuarioUnificadoComprador(candidatos[0]);
        await actualizarContextoComprador(next);
        setCustomer(next);
        return next;
      }

      setCustomerCandidates(candidatos);
      return null;
    } catch (lookupError) {
      setCustomerError(lookupError?.message || "No se pudo consultar el DNI.");
      return null;
    } finally {
      setCustomerPricingLoading(false);
    }
  };

  const useAnonymousConsumer = async () => {
    setCustomerError("");
    setCustomerCandidates([]);
    setCatalogNotice("");
    setCustomerPricingLoading(true);
    try {
      const next = createConsumer();
      await actualizarContextoComprador(next);
      setCustomer(next);
      return next;
    } catch (contextError) {
      setCustomerError(
        contextError?.message || "No se pudo actualizar la tarifa Particular."
      );
      return null;
    } finally {
      setCustomerPricingLoading(false);
    }
  };

  const resetCustomer = async () => {
    setCustomerError("");
    setCustomerCandidates([]);
    setCatalogNotice("");
    setCustomerPricingLoading(true);
    try {
      await actualizarContextoComprador(null);
      setCustomer(null);
    } catch (contextError) {
      setCustomerError(
        contextError?.message || "No se pudo restaurar la tarifa Particular."
      );
    } finally {
      setCustomerPricingLoading(false);
    }
  };

  // El vendedor declara la pieza física recibida. Para un voucher de producto
  // se asegura que el carrito tenga las unidades que cubre, igual que en la demo.
  const selectVoucher = (code) => {
    if (!code) {
      setVoucherCode("");
      return { applied: true };
    }
    const nextVoucher = vouchers.find((item) => item.code === code);
    if (!nextVoucher) return { applied: false, message: "No encontramos el voucher seleccionado." };
    if (getVoucherAvailableValue(nextVoucher, nextVoucher.units ?? nextVoucher.amount) <= 0) {
      return { applied: false, message: "El voucher no tiene saldo disponible para esta venta." };
    }

    if (nextVoucher.type === "PRODUCTO" && nextVoucher.productId) {
      const productId = nextVoucher.productId;
      const missingUnits = Math.max(Number(nextVoucher.units || 0) - Number(cart[productId] || 0), 0);
      const product = products.find((item) => Number(item.id) === Number(productId));
      const stockTeorico = Number(product?.stockTeorico);
      const disponiblesParaAgregar = stockTeorico - Number(cart[productId] || 0);
      if (!Number.isFinite(stockTeorico) || missingUnits > disponiblesParaAgregar) {
        return {
          applied: false,
          message: `Stock insuficiente para aplicar el voucher. Disponible: ${Math.max(disponiblesParaAgregar || 0, 0)}.`,
        };
      }
      if (missingUnits > 0) {
        setCart((current) => ({
          ...current,
          [productId]: Number(current[productId] || 0) + missingUnits,
        }));
        setSelectionOrder((current) => [
          ...current,
          ...Array.from({ length: missingUnits }, () => productId),
        ]);
      }
    }

    setVoucherCode(code);
    return { applied: true };
  };

  const clearSale = () => {
    setCart({});
    setSelectionOrder([]);
    setCustomer(null);
    setCompradorContexto(null);
    setCustomerCandidates([]);
    setPaymentCode("");
    setVoucherCode("");
    setCustomerError("");
    setCatalogNotice("");
    setObservaciones("");
    // Una intención de cobro nueva usa una clave nueva.
    setClaveIdempotencia(nuevaClaveIdempotencia());
    // Actualizar saldos al iniciar otra venta, sin recotizar la que ya cerro.
    refetch({ comprador: null }).catch(() => {});
  };

  // Una linea por producto, en orden de primera incorporacion; todas sus
  // unidades se agrupan como en getVoucherCoverage y en la evaluacion de API.
  const itemsEnOrdenDeSeleccion = () => {
    const primeraAparicion = new Map();
    selectionOrder.forEach((id, index) => {
      if (!primeraAparicion.has(id)) primeraAparicion.set(id, index);
    });
    return [...cartItems]
      .sort(
        (a, b) =>
          (primeraAparicion.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
          (primeraAparicion.get(b.id) ?? Number.MAX_SAFE_INTEGER)
      )
      .map((item) => ({ idProducto: String(item.id), cantidad: item.quantity }));
  };

  const completeSale = async () => {
    const cobraRemanente = totals.amountDue > 0.01;
    let resultado;
    try {
      const response = await ejecutarVenta({
        variables: {
          claveIdempotencia,
          items: itemsEnOrdenDeSeleccion(),
          medioPago: cobraRemanente ? paymentCode : null,
          confirmadoPorVendedor: true,
          dni: customer?.dni || null,
          comprador: referenciaCompradorEvento(customer),
          idVoucher: voucher ? String(voucher.id) : null,
          observaciones: observaciones.trim() || null,
          // Total bruto que ve el vendedor: si la configuración cambió
          // precios mientras armaba la venta, el backend no cobra otro monto.
          totalEsperado: Number(totals.total.toFixed(2)),
          importeCobroEsperado: Number(totals.amountDue.toFixed(2)),
        },
      });
      resultado = response.data;
    } catch (saleError) {
      const code = getErrorCode(saleError);
      const voucherNoDisponible = ["VOUCHER_NO_DISPONIBLE", "EVENTO_COMEDOR_VOUCHER_INVALIDO"].includes(code);
      const cambioVoucher = voucherNoDisponible || [
        "EVENTO_COMEDOR_IMPORTE_CAMBIADO", "VOUCHER_DESCUENTO_CAMBIO", "VOUCHER_SIN_SALDO",
        "VOUCHER_PAPEL_SIN_SALDO", "VOUCHER_PAPEL_INCOMPLETO",
      ].includes(code);
      if (cambioVoucher) {
        // La confirmacion anterior deja de ser valida aunque falle el refresco.
        saleError.requiereReconfirmacion = true;
        if (voucherNoDisponible) setVoucherCode("");
        setCatalogNotice(voucherNoDisponible
          ? "El voucher ya no está disponible. Elegí otro voucher o revisá el importe con el cliente antes de confirmar."
          : "El voucher requiere un papel completo. Revisá los productos, el saldo y el nuevo importe con el cliente antes de confirmar.");
      }
      const cambioConfiguracion = [
        "EVENTO_COMEDOR_PRECIO_CAMBIADO",
        "EVENTO_COMEDOR_MEDIO_NO_HABILITADO",
      ].includes(code);
      if (
        cambioConfiguracion || cambioVoucher ||
        [
          "EVENTO_COMEDOR_PRODUCTO_FUERA_DE_CATALOGO",
          "EVENTO_COMEDOR_PRODUCTO_SIN_PRECIO",
          "EVENTO_COMEDOR_MENU_SIN_PRECIO",
        ].includes(code)
      ) {
        try {
          const contextoRefrescado = await refetch({
            comprador: referenciaCompradorEvento(customer),
          });
          const contextoActualizado = contextoRefrescado?.data?.contextoPuntoVentaEvento;
          const quitados = reconciliarCarritoConContexto(contextoActualizado);
          if (cambioVoucher) {
            const vigente = (contextoActualizado?.vouchersFisicos || [])
              .find((item) => String(item.idVoucher) === voucherCode);
            if (!vigente || getVoucherAvailableValue(mapVoucherFisico(vigente), vigente.valorPieza) <= 0) {
              setVoucherCode("");
            }
          }
          // El aviso del carrito retirado tiene prioridad; si no, se explica
          // que cambiaron precios o medios y que hay que confirmar de nuevo.
          if (cambioConfiguracion && !quitados.length) {
            const medioVigente = (contextoActualizado?.mediosPago || [])
              .some((medio) => medio.codigo === paymentCode);
            if (!medioVigente) setPaymentCode("");
            setCatalogNotice(
              code === "EVENTO_COMEDOR_PRECIO_CAMBIADO"
                ? "Los precios del evento cambiaron. Revisá el nuevo total con el cliente y confirmá de nuevo."
                : "Los medios de pago del evento cambiaron. Elegí otro medio y confirmá de nuevo."
            );
          }
        } catch {
          // Se conserva el error autoritativo de la venta, que explica por que
          // no se persistio; el reintento manual sigue disponible en pantalla.
        }
      }
      throw saleError;
    }

    const venta = resultado?.iniciarVentaEvento;
    // El saldo que acaba de consumirse corresponde a la proxima venta.
    // Conservar la cotizacion confirmada hasta "Iniciar nueva venta" evita
    // mostrar el remanente de la tanda como si fuera el descuento ya aplicado.
    await refetchVentas();

    return {
      id: venta?.nroPedido ? `EV-${venta.nroPedido}` : `EV-${venta?.idPedido}`,
      idPedido: venta?.idPedido || null,
      time: new Date().toLocaleTimeString("es-AR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      customer: customer?.isCommunityMember
        ? `${customer.firstName} ${customer.lastName}`.trim()
        : "Consumidor final",
      items: cartItems.reduce((sum, item) => sum + item.quantity, 0),
      payment: cobraRemanente
        ? paymentMethods.find((item) => item.code === paymentCode)?.label || "—"
        : "No requerido",
      benefit: voucher?.name || null,
      discount: Number(venta?.descuento || 0),
      descuentoVoucher: Number(venta?.descuentoVoucher || 0),
      descuentoEventoMasivo: Number(venta?.descuentoEventoMasivo || 0),
      total: Number(venta?.total || 0),
      status: "Aprobado",
      // Lo que decide si el diálogo imprime solo: una clave repetida devuelve
      // la operación original y no vuelve a imprimir.
      reutilizada: Boolean(venta?.reutilizada),
      documentosImpresion: venta?.documentosImpresion || null,
    };
  };

  const obtenerDocumentosImpresion = async (idPedido) => {
    const { data: resultado, error: errorConsulta } = await consultarDocumentos({
      variables: { idPedido: String(idPedido) },
    });
    if (errorConsulta) throw errorConsulta;
    const dto = resultado?.documentosImpresionVentaEvento;
    if (!dto) throw new Error("No se encontraron los documentos de la venta.");
    return dto;
  };

  return {
    // contexto del evento
    loading: loadingInicial,
    error,
    refetch,
    habilitado: Boolean(contexto?.habilitado),
    evento: contexto?.evento || null,
    vendedor: contexto?.vendedor || null,
    sede: contexto?.sede || null,
    categorias: contexto?.categorias || [],
    avisos,
    saleLoading,

    // contrato compartido con la demo
    products,
    customers: [],
    paymentMethods,
    vouchers,
    cart,
    cartItems,
    customer,
    customerCandidates,
    customerLoading: customerLookupLoading || customerPricingLoading,
    customerError,
    paymentCode,
    voucherCode,
    voucher,
    observaciones,
    totals,
    recentSales,
    addProduct,
    changeQuantity,
    identifyCustomer,
    selectCustomerCandidate,
    resetCustomer,
    useAnonymousConsumer,
    setPaymentCode,
    setObservaciones,
    selectVoucher,
    clearSale,
    completeSale,
    obtenerDocumentosImpresion,
  };
}
